// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartGpsCompassTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Smart GPS Compass"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Smart
        GPS Compass” (the “App”). By downloading, installing, accessing, or
        using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>
        2) Description of the App (Non-Professional Use)
      </div>
      <p>
        The App provides a digital compass and basic GPS information, such as
        cardinal directions, heading in degrees, and approximate location
        coordinates. It may also display signal accuracy estimates, support
        light/dark themes, and offer cosmetic customization options. The App is
        intended for general, consumer-level orientation and convenience only.
        It is not a professional navigation system and must not be relied upon
        for safety-critical uses, including aviation, maritime navigation,
        emergency response, or any situation where inaccurate information could
        cause harm.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Location, Sensors, and Permissions</div>
      <p>
        To provide compass and GPS functionality, the App may request access to
        location services and device sensors (such as magnetometer and motion
        sensors). You can choose whether to grant or revoke these permissions in
        your device settings. If you deny permissions, some or all features may
        be unavailable.
      </p>
      <p>
        Sensor readings and location data are subject to limitations and
        interference, including device hardware quality, calibration, nearby
        magnetic or metal objects, buildings, weather, and OS-level
        restrictions. The App may show approximations rather than precise
        measurements. You are responsible for calibrating your device where
        necessary and for cross-checking information with other reliable
        sources.
      </p>

      <div className={s.title}>5) Free and PRO Features</div>
      <p>
        The App may offer a free tier and a PRO tier. The free version generally
        includes a basic compass and GPS display, and may show advertising if
        enabled. The PRO version may unlock additional features such as
        cosmetic themes, removal of in-app advertising, or other enhancements
        described in the App or on the paywall screen. The exact feature set for
        each tier may change over time as the App is updated.
      </p>

      <div className={s.title}>6) Subscriptions, Trials, and Billing</div>
      <p>
        PRO features may be available through paid auto-renewable subscriptions
        or other in-app purchases (“Subscriptions”), such as weekly and/or
        yearly plans. Exact pricing, plan names, and trial availability may vary
        by region and may be updated over time.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are handled according
        to the policies of the relevant app store.
      </p>

      <div className={s.title}>7) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to PRO features
        such as additional themes or ad removal. An internet connection may be
        required to validate or refresh entitlements after long offline periods.
        If platform billing reverses, refunds, or flags a transaction as
        fraudulent, related entitlements may be limited, suspended, or revoked.
      </p>

      <div className={s.title}>8) Accuracy, Calibration, and Limitations</div>
      <p>
        Compass and location readings provided by the App are approximate and
        may be inaccurate or unavailable under certain conditions. For example,
        nearby magnets, metal surfaces, cases, electronic devices, buildings, or
        environmental factors can distort readings. The App may show instructions
        or tips for calibrating your device, but calibration quality depends on
        hardware, OS, and user actions.
      </p>
      <p>
        You must not rely on the App as the sole means of navigation or
        orientation in situations where incorrect readings could result in
        property damage, personal injury, or safety risks. Always use additional
        tools and sources (such as official maps, signs, or dedicated navigation
        devices) and exercise judgment.
      </p>

      <div className={s.title}>9) Acceptable Use</div>
      <p>
        You agree to use the App only for lawful, personal purposes and not to:
      </p>
      <ul>
        <li>
          use the App in ways that violate applicable laws, regulations, or
          third-party rights;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          interfere with or disrupt servers, networks, or services used by the
          App;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and PRO features;
        </li>
        <li>
          misrepresent the App&apos;s readings as certified or professional
          measurements (for example, for official surveying, aviation, or
          maritime navigation).
        </li>
      </ul>

      <div className={s.title}>10) Advertising and Promotions</div>
      <p>
        In some versions, the free tier of the App may include advertising or
        promotional content. Advertising may be delivered through third-party
        ad networks subject to their own terms and policies. If you purchase or
        activate a PRO plan that removes ads, the App will attempt to disable
        in-app advertising for your account or device, though platform-level
        promotions or store banners not controlled by the App may still appear.
        Where required by law, we will handle consent for any personalized
        advertising separately from these Terms.
      </p>

      <div className={s.title}>11) Analytics and Performance Monitoring</div>
      <p>
        We may use analytics and crash-reporting tools (for example, Firebase
        Analytics and Crashlytics or similar services) to understand how the App
        is used, to monitor performance, and to improve reliability and user
        experience. Typical events may include app launches, screen views,
        theme changes, paywall views, and purchases. Aggregated statistics may
        be used to guide product decisions. Where required by law, we will
        obtain necessary permissions or provide options to manage analytics
        preferences.
      </p>

      <div className={s.title}>12) Intellectual Property</div>
      <p>
        All software, interfaces, graphics, icons, gradients, designs, logos,
        and other materials provided with the App (excluding your device data
        and any separately owned content) are owned by the publisher or its
        licensors and are protected by intellectual property laws. Subject to
        these Terms and any Subscription limitations, you are granted a limited,
        non-exclusive, non-transferable, revocable license to use the App and
        its features solely for personal orientation and informational purposes.
        You may not resell, redistribute, or sublicense the App or its assets as
        a separate product.
      </p>

      <div className={s.title}>13) Third-Party Services and Libraries</div>
      <p>
        The App may use third-party services and libraries for location,
        sensor access, analytics, crash reporting, and subscription management.
        Examples include system location APIs, sensor frameworks, Firebase
        services, and RevenueCat. Technical data and usage information may be
        shared with these providers to operate and improve the App. These
        providers act under their own terms and privacy policies, and we require
        them by contract to handle data appropriately and only for allowed
        purposes.
      </p>

      <div className={s.title}>14) No Warranties; Service Changes</div>
      <p>
        We aim to provide smooth and visually accurate compass animations and
        GPS displays, but we do not guarantee that readings will always be
        available, precise, or error-free. The App and its features may change,
        be updated, or be removed over time. We may add, modify, or discontinue
        free or PRO features as part of ongoing development, subject to
        applicable consumer-protection laws.
      </p>

      <div className={s.title}>15) Limitation of Liability</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. To the extent allowed by law, we are not liable for
        indirect, incidental, consequential, or punitive damages, loss of
        profits, data loss, or business interruption arising from or related to
        your use of or inability to use the App, including any reliance on
        compass or GPS readings. Some jurisdictions do not allow certain
        limitations or exclusions, so parts of this section may not apply to
        you.
      </p>

      <div className={s.title}>16) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your breach of these Terms, or your
        violation of any rights of a third party, including misuse of readings
        in contexts where professional-grade equipment is required.
      </p>

      <div className={s.title}>17) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>18) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Smart GPS Compass, which explains how information is collected, used,
        stored, and shared, including technical and analytics data. In case of
        conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will guide how we handle personal data,
        while these Terms govern your use of the App and its features. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>19) Changes to These Terms</div>
      <p>
        We may update the App, add or remove features (including free and PRO
        capabilities), or modify these Terms from time to time. The “Last
        updated” date at the top of this document indicates when these Terms
        were last revised. Material changes may be highlighted in the App or via
        other reasonable means. By continuing to use the App after changes take
        effect, you accept the updated Terms. If you do not agree to the revised
        Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>20) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
